import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronUp, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { adminChatLeads, adminChatLeadFollowUp } from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "-");
const TABS = [["open", "To follow up"], ["followed", "Followed up"], ["all", "All"]];

const LeadCard = ({ lead, i, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fu = lead.followed_up;

  const mark = async (followed) => {
    setBusy(true);
    try {
      await adminChatLeadFollowUp(lead.session_id, followed, note);
      toast.success(followed ? "Marked as followed up" : "Moved back to follow-up list");
      setNote("");
      onChanged();
    } catch (e) {
      console.error("[chat-leads] update failed", e);
      toast.error("Could not update this chat. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-white" data-testid={`chat-lead-${i}`}>
      <div className="px-5 py-4 flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="font-medium text-brand-black" data-testid={`chat-lead-contacts-${i}`}>{lead.contacts.join(" · ") || "Contact details"}</p>
          <p className="text-xs text-muted-foreground">
            Started {fmt(lead.created_at)} · Last message {fmt(lead.updated_at)}
            {lead.reference ? ` · Enquiry submitted (${lead.reference})` : " · No enquiry submitted"}
          </p>
          {fu && (
            <p className="text-xs text-brand-darkgrey" data-testid={`chat-lead-followed-${i}`}>
              <Check className="inline h-3.5 w-3.5 text-brand-gold mr-1" />Followed up by {fu.by} on {fmt(fu.at)}{fu.note ? ` — ${fu.note}` : ""}
            </p>
          )}
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`chat-lead-toggle-${i}`}
          className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {open ? "Hide" : "View"} conversation
        </button>
      </div>
      {open && (
        <div className="border-t border-border px-5 py-4 space-y-2 bg-brand-grey/20 max-h-96 overflow-y-auto" data-testid={`chat-lead-transcript-${i}`}>
          {lead.transcript.map((ln, j) => (
            <p key={j} className={`text-sm whitespace-pre-wrap ${ln.startsWith("Visitor:") ? "text-brand-black" : "text-brand-darkgrey"}`}>{ln}</p>
          ))}
        </div>
      )}
      <div className="border-t border-border px-5 py-3 flex flex-wrap items-end gap-3">
        {!fu ? (
          <>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} placeholder="Optional note (e.g. called, left voicemail)"
              data-testid={`chat-lead-note-${i}`} className="flex-1 min-w-[220px] text-sm rounded-sm" />
            <Button onClick={() => mark(true)} disabled={busy} data-testid={`chat-lead-mark-${i}`} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm">
              <Check className="h-4 w-4 mr-1" /> Mark followed up
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => mark(false)} disabled={busy} data-testid={`chat-lead-reopen-${i}`} className="rounded-sm">
            <RotateCcw className="h-4 w-4 mr-1" /> Move back to follow-up
          </Button>
        )}
      </div>
    </div>
  );
};

export default function ChatLeads() {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState({ items: [], open_count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminChatLeads(status));
    } catch (e) {
      console.error("[chat-leads] load failed", e);
      toast.error("Could not load chats.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-brand-grey/30">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <p className="font-serif text-xl text-brand-black">Family Court Guide — Chat leads</p>
          <Link to="/admin/enquiries" data-testid="nav-back-enquiries" className="text-sm text-brand-darkgrey hover:text-brand-gold inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Enquiries
          </Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        <p className="text-sm text-brand-darkgrey">Web chats where the visitor typed an email address or phone number.</p>
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`chat-leads-tab-${k}`}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${status === k ? "bg-brand-black text-white border-brand-black" : "bg-white border-border text-brand-darkgrey hover:border-brand-gold"}`}>
              {label}{k === "open" ? ` (${data.open_count})` : ""}
            </button>
          ))}
          <Button variant="ghost" onClick={load} data-testid="chat-leads-refresh" className="ml-auto text-brand-darkgrey rounded-sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground" data-testid="chat-leads-loading">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border bg-white px-5 py-8 text-center" data-testid="chat-leads-empty">
            {status === "open" ? "Nothing to follow up — all caught up." : "No chats here yet."}
          </p>
        ) : (
          <div className="space-y-4" data-testid="chat-leads-list">
            {data.items.map((lead, i) => <LeadCard key={lead.session_id} lead={lead} i={i} onChanged={load} />)}
          </div>
        )}
      </main>
    </div>
  );
}
